import type { Onboarding } from '@catalyst-psa/shared';
import type { JSX } from 'react';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { formatDate } from '../../utils/date.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { useOnboardings } from './api.js';
import { ProgressCell } from './OnboardingPage.js';
import {
  ONBOARDING_PHASE_LABELS,
  ONBOARDING_PHASE_TONES,
  ONBOARDING_STATUS_LABELS,
  ONBOARDING_STATUS_TONES,
} from './status.js';

const HEAD_CLASS = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500';
const CELL_CLASS = 'px-3 py-2 text-sm text-slate-700';

/**
 * The onboarding records tab shared by the customer and opportunity forms. Pass exactly
 * one scope: a customer (all of its onboardings) or an opportunity (at most one, since
 * onboarding is 1:1 with the opportunity). Rows open the record on the onboarding page
 * (ctrl/middle-click opens it in a new tab).
 */
export function OnboardingRecordsTab({
  customerId,
  opportunityId,
}: {
  customerId?: string;
  opportunityId?: string;
}): JSX.Element {
  const rowLink = useRowLink();
  const query = useOnboardings({ page: 1, pageSize: 200, customerId, opportunityId });
  const rows = query.data?.data ?? [];
  // Scoped to one opportunity that column would just repeat, so show the customer.
  const byOpportunity = Boolean(opportunityId);
  const head = [
    byOpportunity ? 'Customer' : 'Opportunity',
    'Progress',
    'Status',
    'Delivery phase',
    'Created',
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full border-collapse">
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr>
            {head.map((h) => (
              <th key={h} className={HEAD_CLASS}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <DataState
            colSpan={head.length}
            isLoading={query.isLoading}
            isError={query.isError}
            error={query.error}
            isEmpty={rows.length === 0}
            emptyTitle={
              byOpportunity
                ? 'No onboarding record for this opportunity.'
                : 'No onboarding records for this customer.'
            }
            emptyDescription="An onboarding record is created when an opportunity is set to Order Approved."
            onRetry={() => void query.refetch()}
          >
            {rows.map((onboarding: Onboarding) => (
              <tr
                key={onboarding.id}
                {...rowLink(`/sales/onboarding/${onboarding.id}`)}
                className="cursor-pointer hover:bg-slate-50"
              >
                <td className={`${CELL_CLASS} font-medium text-slate-900`}>
                  {byOpportunity ? onboarding.customerName : onboarding.opportunityName}
                </td>
                <td className={CELL_CLASS}>
                  <ProgressCell onboarding={onboarding} />
                </td>
                <td className={CELL_CLASS}>
                  <Badge tone={ONBOARDING_STATUS_TONES[onboarding.status]}>
                    {ONBOARDING_STATUS_LABELS[onboarding.status]}
                  </Badge>
                </td>
                <td className={CELL_CLASS}>
                  {onboarding.phase ? (
                    <Badge tone={ONBOARDING_PHASE_TONES[onboarding.phase]}>
                      {ONBOARDING_PHASE_LABELS[onboarding.phase]}
                    </Badge>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className={CELL_CLASS}>{formatDate(onboarding.createdAt)}</td>
              </tr>
            ))}
          </DataState>
        </tbody>
      </table>
    </div>
  );
}
